import axios from 'axios'

import { ServerValidationError } from '@/utils/errors'
import MsalAuthentication from '@/msal-auth'
import CryptoJS from 'crypto-js'

export default class BaseService {
  getUserEmail () {
    let useremail = ''

    const encryptedUser = localStorage.getItem('custom-user')

    if (encryptedUser) {
      try {
        const decryptedUser = CryptoJS.AES.decrypt(encryptedUser, 'secret key')
        useremail = decryptedUser.toString(CryptoJS.enc.Utf8)
      } catch (error) {
        useremail = ''
      }
    }

    if (!useremail) {
      useremail = localStorage.getItem('useremail') || ''
    }

    if (!useremail && MsalAuthentication.getUserEmail) {
      useremail = MsalAuthentication.getUserEmail()
    }

    return useremail
  }

  isTokenExpired (token) {
    try {
      if (!token) {
        return true
      }

      const payloadPart = token.split('.')[1]

      if (!payloadPart) {
        return true
      }

      const base64 = payloadPart
        .replace(/-/g, '+')
        .replace(/_/g, '/')

      const paddedBase64 = base64.padEnd(
        base64.length + (4 - base64.length % 4) % 4,
        '='
      )

      const payload = JSON.parse(atob(paddedBase64))

      if (!payload.exp) {
        return true
      }

      return Date.now() >= payload.exp * 1000
    } catch (error) {
      return true
    }
  }

  clearAuthAndRedirect () {
    sessionStorage.clear()

    localStorage.removeItem('user-token')
    localStorage.removeItem('custom-user')
    localStorage.removeItem('useremail')
    localStorage.removeItem('sso-user-email')
    localStorage.removeItem('api_token_redirect_started')

    window.location.href = '/login'
  }

  handleError (error, reject) {
    if (error.response) {
      const tokenExpired = error.response.headers &&
        error.response.headers['token-expired'] === 'true'

      if (error.response.status === 401 || tokenExpired) {
        this.clearAuthAndRedirect()
        reject(new Error('Unauthorized or token expired'))
        return
      }

      if (error.response.status === 400) {
        reject(new ServerValidationError(error.response.data))
        return
      }

      if (error.response.status === 500) {
        if (error.response.data && error.response.data.detail) {
          reject(new Error(error.response.data.detail))
        } else {
          reject(error)
        }

        return
      }

      reject(error)
      return
    }

    reject(new Error(error))
  }

  async getHeaders (contentType, additionalHeaders) {
    let headers = {
      'content-type': contentType
    }

    const useremail = this.getUserEmail()
    const appToken = localStorage.getItem('user-token')

    if (appToken !== null) {
      if (this.isTokenExpired(appToken)) {
        this.clearAuthAndRedirect()
        throw new Error('Token expired')
      }

      headers = {
        ...headers,
        Authorization: `Bearer ${appToken}`
      }
    } else {
      const accessToken = await MsalAuthentication.acquireToken()

      if (!accessToken) {
        throw new Error('MSAL access token not available')
      }

      headers = {
        ...headers,
        Authorization: `Bearer ${accessToken}`,
        'MsGraph-Auth-Token': accessToken
      }
    }

    if (useremail) {
      headers.useremail = useremail
    }

    if (additionalHeaders) {
      for (const headerKey in additionalHeaders) {
        headers[headerKey] = additionalHeaders[headerKey]
      }
    }

    return headers
  }

  async sendRequest (httpMethod, url, data, additionalHeaders, responseType) {
    let headers

    try {
      headers = await this.getHeaders('application/json', additionalHeaders)
    } catch (error) {
      return Promise.reject(error)
    }

    return new Promise((resolve, reject) => {
      axios.request({
        method: httpMethod,
        url,
        data,
        headers,
        responseType: responseType || 'json'
      }).then((result) => {
        resolve(result)
      }).catch((error) => {
        this.handleError(error, reject)
      })
    })
  }

  async sendFormRequest (httpMethod, url, data, additionalHeaders, responseType) {
    let headers

    try {
      headers = await this.getHeaders('multipart/form-data', additionalHeaders)
    } catch (error) {
      return Promise.reject(error)
    }

    return new Promise((resolve, reject) => {
      axios.request({
        method: httpMethod,
        url,
        data,
        headers,
        responseType: responseType || 'json'
      }).then((result) => {
        resolve(result)
      }).catch((error) => {
        this.handleError(error, reject)
      })
    })
  }

  async sendNoAuthRequest (httpMethod, url, data, additionalHeaders, responseType) {

    let headers = {
      'content-type': 'application/json'
    }

    for (const headerKey in additionalHeaders) {
      headers[headerKey] = additionalHeaders[headerKey]
    }

    return new Promise((resolve, reject) => {
      axios.request({
        method: httpMethod,
        url,
        data,
        headers: headers,
        responseType: responseType || 'json'
      }).then((result) => {
        resolve(result)
      }).catch((error) => {
        reject(new Error(error))
      })
    })
  }
}